import { PayloadAction } from '@reduxjs/toolkit';
import { isAxiosError } from 'axios';
import { all, call, put, takeEvery } from 'redux-saga/effects';
import { api } from 'services';
import { responseErrors } from 'services/http/responseErrors';
import { notificationContainer } from 'untils/notificationContainer';
import {
  approveModerationFailure,
  approveModerationRequest,
  approveModerationSuccess,
  getModerationChannelsFailure,
  getModerationChannelsRequest,
  getModerationChannelsSuccess,
  getModerationFlagsFailure,
  getModerationFlagsRequest,
  getModerationFlagsSuccess,
  getModerationReportsCountersFailure,
  getModerationReportsCountersRequest,
  getModerationReportsCountersSuccess,
  getModerationReportsFailure,
  getModerationReportsRequest,
  getModerationReportsSuccess,
  getModerationUsersFailure,
  getModerationUsersRequest,
  getModerationUsersSuccess,
  getTopChannelsFailure,
  getTopChannelsRequest,
  getTopChannelsSuccess,
  getTopUsersFailure,
  getTopUsersRequest,
  getTopUsersSuccess,
  moderationInitState,
  rejectModerationFailure,
  rejectModerationRequest,
  rejectModerationSuccess,
} from './reducer';
import {
  ApproveModerationRequest,
  ApproveModerationResponse,
  GetChannelsRequest,
  GetChannelsResponse,
  GetFlagsResponse,
  GetReportsCountersResponse,
  GetReportsRequest,
  GetReportsResponse,
  GetUsersRequest,
  GetUsersResponse,
  ITopChannelResponse,
  ITopUsersResponse,
  RejectModerationRequest,
  RejectModerationResponse,
} from './types';

// --- Workers ---

function* getModerationReportsCountersWorker() {
  try {
    const data: GetReportsCountersResponse = yield call(api.moderation.getCounters);
    yield put(getModerationReportsCountersSuccess(data));
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
    yield put(getModerationReportsCountersFailure());
  } finally {
    yield put(moderationInitState());
  }
}

function* getModerationReportsWorker(action: PayloadAction<GetReportsRequest>) {
  try {
    const data: GetReportsResponse = yield call(api.moderation.getReports, action.payload);
    yield put(getModerationReportsSuccess(data));
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
    yield put(getModerationReportsFailure());
  } finally {
    yield put(moderationInitState());
  }
}

function* getModerationFlagsWorker() {
  try {
    const data: GetFlagsResponse = yield call(api.moderation.getFlags);
    yield put(getModerationFlagsSuccess(data));
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
    yield put(getModerationFlagsFailure());
  } finally {
    yield put(moderationInitState());
  }
}

function* approveModerationWorker(action: PayloadAction<ApproveModerationRequest>) {
  try {
    const data: ApproveModerationResponse = yield call(api.moderation.approve, action.payload);
    notificationContainer('Approve is successfully', 'success');

    yield put(approveModerationSuccess(data.data));
  } catch (error) {
    notificationContainer('Approve failed', 'error');

    if (isAxiosError(error)) responseErrors(error);
    yield put(approveModerationFailure());
  } finally {
    yield put(moderationInitState());
  }
}

function* rejectModerationWorker(action: PayloadAction<RejectModerationRequest>) {
  try {
    const data: RejectModerationResponse = yield call(api.moderation.reject, action.payload);
    notificationContainer('Reject is successfully', 'success');

    yield put(rejectModerationSuccess(data.data));
  } catch (error) {
    notificationContainer('Reject failed', 'error');
    if (isAxiosError(error)) responseErrors(error);
    yield put(rejectModerationFailure());
  } finally {
    yield put(moderationInitState());
  }
}

function* getModerationUsersWorker(action: PayloadAction<GetUsersRequest>) {
  try {
    const data: GetUsersResponse = yield call(api.moderation.getUsers, action.payload);
    yield put(getModerationUsersSuccess(data));
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
    yield put(getModerationUsersFailure());
  } finally {
    yield put(moderationInitState());
  }
}

function* getModerationChannelsWorker(action: PayloadAction<GetChannelsRequest>) {
  try {
    const data: GetChannelsResponse = yield call(api.moderation.getChannels, action.payload);
    yield put(getModerationChannelsSuccess(data));
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
    yield put(getModerationChannelsFailure());
  } finally {
    yield put(moderationInitState());
  }
}

function* getTopChannelsWorker() {
  try {
    const data: ITopChannelResponse = yield call(api.moderation.topChannels);
    yield put(getTopChannelsSuccess(data));
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
    yield put(getTopChannelsFailure());
  } finally {
    yield put(moderationInitState());
  }
}

function* getTopUsersWorker() {
  try {
    const data: ITopUsersResponse = yield call(api.moderation.topUsers);
    yield put(getTopUsersSuccess(data));
  } catch (error) {
    if (isAxiosError(error)) responseErrors(error);
    yield put(getTopUsersFailure());
  } finally {
    yield put(moderationInitState());
  }
}

// --- Root saga for moderation ---

export function* moderationSaga() {
  yield all([
    // ВАЖЛИВО: передаємо **екшн-кріейтор**, а не .type
    takeEvery(getModerationReportsCountersRequest, getModerationReportsCountersWorker),
    takeEvery(getModerationReportsRequest, getModerationReportsWorker),
    takeEvery(getModerationFlagsRequest, getModerationFlagsWorker),
    takeEvery(approveModerationRequest, approveModerationWorker),
    takeEvery(rejectModerationRequest, rejectModerationWorker),
    takeEvery(getModerationUsersRequest, getModerationUsersWorker),
    takeEvery(getModerationChannelsRequest, getModerationChannelsWorker),
    takeEvery(getTopChannelsRequest, getTopChannelsWorker),
    takeEvery(getTopUsersRequest, getTopUsersWorker),
  ]);
}
